import { lazy, Suspense, useEffect, useState } from "react";
import {
  BrowserRouter, Routes, Route, Navigate, NavLink, Outlet,
  useNavigate, useParams, useLocation,
} from "react-router-dom";
import { api, setToken, type Club, type User } from "./api.js";
import { exportAllPending } from "./outbox.js";
import { countPending } from "./sync.js";
import { cacheGet, cacheSet, invalidate } from "./outbox.js";
import { startSync, type SyncReport } from "./sync.js";
import { onUpdate, update, BUILD } from "./updates.js";
import { Mark } from "./Mark.js";
/**
 * Screens a scorer does not need in order to score.
 *
 * Everything was in one 557 KB chunk, so opening a match downloaded the
 * tournament rules editor, the audio library and the repair tools first. At a
 * ground on mobile data that is the difference between the keypad arriving and
 * the scorer waiting.
 *
 * The scoring path — Scorer, Watch, Matches, Home — stays eager on purpose.
 * A split chunk that has to be fetched mid-over is worse than one that was
 * already there.
 */
/**
 * Warm the split chunks once the app is idle.
 *
 * The service worker precaches all of them, so from the second visit they are
 * local — but on a first cold entry to /cups the browser fetches the main
 * bundle, parses it, discovers the import, and only then asks for the chunk.
 * That second round trip is visible on mobile data.
 *
 * requestIdleCallback so it never competes with the scoring path: these are the
 * screens a scorer does NOT need, and prefetching them ahead of the keypad
 * would defeat the point of splitting them out. Failures are ignored — a warm
 * cache is a courtesy, and the route still loads on demand.
 */
function warmChunks(): void {
  const idle = (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback
            ?? ((cb: () => void) => setTimeout(cb, 2500));
  idle(() => {
    void import("./screens/Cups.js").catch(() => {});
    void import("./screens/Audio.js").catch(() => {});
    void import("./screens/Player.js").catch(() => {});
    void import("./screens/Team.js").catch(() => {});
  });
}

const Cups = lazy(() => import("./screens/Cups.js").then(m => ({ default: m.Cups })));
const Cup = lazy(() => import("./screens/Cups.js").then(m => ({ default: m.Cup })));
const Members = lazy(() => import("./screens/Members.js").then(m => ({ default: m.Members })));
const Admin = lazy(() => import("./screens/Admin.js").then(m => ({ default: m.Admin })));
const Audio = lazy(() => import("./screens/Audio.js").then(m => ({ default: m.Audio })));
const Player = lazy(() => import("./screens/Player.js").then(m => ({ default: m.Player })));
const Team = lazy(() => import("./screens/Team.js").then(m => ({ default: m.Team })));

import { Clock } from "./Clock.js";
import {
  IconHome, IconMatch, IconPeople, IconTeam, IconTrophy, IconMusic, IconSettings,
  IconLive, IconChevron, IconWatch, IconChart, IconLogout,
} from "./Icons.js";
import { Auth } from "./screens/Auth.js";
import { People } from "./screens/People.js";
import { Teams } from "./screens/Teams.js";
import { Matches } from "./screens/Matches.js";
import { Scorer } from "./screens/Scorer.js";
import { Watch } from "./screens/Watch.js";
import { VoiceBar } from "./screens/VoiceBar.js";
import { OfflineAudio } from "./screens/Offline.js";
import type { Lang } from "./commentary.js";
import { Screen, Card, Row, Button, Field, Empty, Skeleton } from "./ui.js";

/**
 * Routing.
 *
 * Every screen has a URL. The scorer used to be component state, so reloading
 * mid-match — or a phone deciding to reload the tab on its own, which they do —
 * dropped you back at Home. On a ground with one bar that is exactly when it
 * happens.
 *
 * It also makes a live score shareable: /score/<matchId> survives being sent to
 * someone, and the lease decides what they can do with it.
 */

const NAV = [
  { to: "/", label: "Home", end: true, Icon: IconHome },
  { to: "/matches", label: "Matches", end: false, Icon: IconMatch },
  { to: "/people", label: "People", end: false, Icon: IconPeople },
  { to: "/teams", label: "Teams", end: false, Icon: IconTeam },
  { to: "/cups", label: "Cups", end: false, Icon: IconTrophy },
  { to: "/audio", label: "Audio", end: false, Icon: IconMusic },
  { to: "/more", label: "Settings", end: false, Icon: IconSettings },
] as const;

export function App() {
  return (
    <BrowserRouter>
      <Root />
    </BrowserRouter>
  );
}

function Root() {
  const [user, setUser] = useState<User | null>(null);
  const [club, setClub] = useState<Club | null>(null);
  const [ready, setReady] = useState(false);
  const [queued, setQueued] = useState(0);

  const boot = async () => {
    // Offline-first: if we knew who you were last time, show the app now and
    // confirm behind. Otherwise a cold open with no signal is a spinner that
    // never resolves.
    const cachedUser = await cacheGet<User>("me");
    const cachedClub = await cacheGet<Club>("club");
    if (cachedUser && cachedClub) {
      setUser(cachedUser.value); setClub(cachedClub.value); setReady(true);
    }

    const me = await api<{ user: User | null }>("/api/v1/auth/me").catch(() => null);
    if (!me) { setReady(true); return; }
    const u = me.ok ? me.data.user : null;
    setUser(u);
    if (u) {
      void cacheSet("me", u);
      const cs = await api<{ clubs: Club[] }>("/api/v1/clubs").catch(() => null);
      const c = cs?.ok ? (cs.data.clubs[0] ?? null) : (cachedClub?.value ?? null);
      setClub(c);
      if (c) void cacheSet("club", c);
    } else {
      setClub(null);
    }
    setReady(true);
  };

  useEffect(() => { void boot(); }, []);

  // Sync runs for the life of the app, not inside a screen. Draining only while
  // the scorer was mounted stranded everything the moment you navigated away.
  useEffect(() => {
    if (!user) return;
    warmChunks();
    return startSync((r: SyncReport) => setQueued(r.remaining));
  }, [user]);

  if (!ready) {
    return <div className="flex h-full items-center justify-center text-body text-faint">Loading…</div>;
  }
  if (!user) return <Auth onSignedIn={() => { setReady(false); void boot(); }} />;
  if (!club) return <Empty title="No club">This account is not a member of a club yet.</Empty>;

  const canEdit = ["OWNER", "ADMIN", "ORGANIZER"].includes(club.role);
  // Managing people is a narrower thing than organising cricket.
  const canManage = ["OWNER", "ADMIN"].includes(club.role);
  const canScore = canEdit || club.role === "SCORER";

  return (
    <>
    <UpdateBar />
    <Suspense fallback={<div className="p-4"><Skeleton rows={5} /></div>}>
    <Routes>
      {/* The scorer takes the whole screen: no nav competing for the thumb. */}
      <Route path="/score/:matchId" element={<ScorerRoute />} />
      {/* The shareable one: read-only, no lease, works without an account. */}
      <Route path="/watch/:matchId" element={<Watch />} />
      <Route element={<Shell club={club} queued={queued}
        onSignedOut={() => { setUser(null); setClub(null); }} />}>
        <Route path="/" element={<Home club={club} user={user} />} />
        <Route path="/matches" element={<MatchesRoute clubId={club.id} canScore={canScore} />} />
        <Route path="/people" element={<People clubId={club.id} canEdit={canEdit} />} />
        <Route path="/teams" element={<Teams clubId={club.id} canEdit={canEdit} />} />
        <Route path="/cups" element={<Cups clubId={club.id} canEdit={canEdit} />} />
        <Route path="/cup/:cupId" element={<Cup canEdit={canEdit} />} />
        <Route path="/player/:playerId" element={<Player />} />
        <Route path="/team/:teamId" element={<Team canEdit={canEdit} />} />
        {canManage && <Route path="/members" element={<Members clubId={club.id} />} />}
        {canEdit && <Route path="/repair" element={<Admin clubId={club.id} />} />}
        <Route path="/audio" element={<Audio clubId={club.id} canEdit={canEdit} />} />
        <Route path="/more" element={<More user={user} club={club}
          onSignedOut={() => { setUser(null); setClub(null); }} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
    </Suspense>
    </>
  );
}

/**
 * A new build is ready.
 *
 * Offered, not imposed: reloading the page under a scorer mid-over is worse
 * than running yesterday's build for another ten minutes. It sits at the top,
 * out of the way of the keypad.
 */
function UpdateBar() {
  const [waiting, setWaiting] = useState(false);
  useEffect(() => onUpdate(setWaiting), []);
  if (!waiting) return null;
  return (
    <button onClick={() => void update()}
      className="fixed inset-x-0 top-0 z-50 flex min-h-[42px] items-center justify-center gap-2
                 bg-amber px-4 text-board"
      style={{ paddingTop: "var(--sat)" }}>
      <span className="text-body font-semibold">A new version is ready</span>
      <span className="font-mono text-meta uppercase tracking-[.12em] opacity-70">Tap to load</span>
    </button>
  );
}

/* ── chrome ─────────────────────────────────────────────────────────────── */

function Shell({ club, queued, onSignedOut }: {
  club: Club; queued: number; onSignedOut: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  const signOut = async () => {
    await api("/api/v1/auth/logout", { method: "POST" }).catch(() => { /* offline */ });
    setToken(null);
    onSignedOut();
  };

  return (
    <div className="flex h-full flex-col desk:flex-row">
      <nav className="order-2 flex shrink-0 border-t border-line bg-board pb-[var(--sab)]
                      desk:order-1 desk:w-[220px] desk:flex-col desk:gap-1 desk:border-r
                      desk:border-t-0 desk:p-3 desk:pt-[68px]">
        {NAV.map(({ to, label, end, Icon }) => (
          <NavLink key={to} to={to} end={end}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-[3px] py-2 transition-colors
               desk:flex-none desk:flex-row desk:gap-2.5 desk:rounded-card desk:px-3 desk:py-2.5
               ${isActive ? "text-amber desk:bg-panel" : "text-faint hover:text-chalk"}`}>
            <Icon size={21} />
            {/* The label is small on a phone and full size in the sidebar: at the
                bottom of a screen an icon carries most of the meaning, in a
                sidebar the word does. */}
            {/* Was 9px. The nav is read at arm's length, outdoors, by someone
                holding a phone in one hand and a pen in the other — it is the
                worst place in the app to save two pixels. */}
            <span className="font-mono text-label font-semibold uppercase tracking-[.06em]
                             desk:font-sans desk:text-body desk:normal-case desk:tracking-normal">
              {label}
            </span>
          </NavLink>
        ))}
      </nav>

      <div className="order-1 flex min-h-0 flex-1 flex-col desk:order-2">
        <header className="flex shrink-0 items-center gap-2.5 border-b border-line px-4 pb-2.5
                           pt-[calc(10px+var(--sat))] desk:fixed desk:left-[220px] desk:right-0
                           desk:top-0 desk:z-20 desk:bg-board desk:pt-3">
          <Mark size={34} />
          <span className="font-num text-title font-semibold tracking-[.19em]">ORBIT</span>
          {queued > 0 && (
            <span className="ml-auto shrink-0 rounded-full border border-amber px-2 py-0.5
                             font-mono text-label text-amber">{queued} to send</span>
          )}
          <span className={`hidden truncate font-mono text-meta text-faint split:inline
                            ${queued > 0 ? "ml-2" : "ml-auto"}`}>
            {club.name}
          </span>

          <Clock className={queued > 0 || true ? "ml-auto split:ml-3" : ""} />

          {/* An icon, not a menu item buried in settings — but confirmed,
              because a stray tap here at a ground ends the session and the
              scorer has to sign in again from a phone with no signal. */}
          <button onClick={() => setConfirming(true)} aria-label="Sign out"
            className="ml-1 flex h-11 w-11 shrink-0 items-center justify-center text-faint
                       hover:text-chalk">
            <IconLogout size={19} />
          </button>
        </header>

        {confirming && (
          <div className="border-b border-line bg-panel px-4 py-3">
            {queued > 0 ? (
              <p className="mb-2.5 text-body leading-relaxed text-leather">
                {queued} {queued === 1 ? "delivery has" : "deliveries have"} not reached the
                server yet. Signing out now leaves {queued === 1 ? "it" : "them"} on this
                phone until you sign back in.
              </p>
            ) : (
              <p className="mb-2.5 text-body text-faint">Sign out of {club.name}?</p>
            )}
            <div className="flex gap-2">
              <button onClick={() => setConfirming(false)}
                className="min-h-[44px] flex-1 rounded-control border border-line text-body">
                Stay signed in
              </button>
              <button onClick={() => void signOut()}
                className="min-h-[44px] flex-1 rounded-control border border-[#3A2422]
                           text-body text-leather">
                Sign out
              </button>
            </div>
          </div>
        )}
        <main className="min-h-0 flex-1 overflow-y-auto desk:pt-14"><Outlet /></main>
      </div>
    </div>
  );
}

/* ── routes that need params ────────────────────────────────────────────── */

function ScorerRoute() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  if (!matchId) return <Navigate to="/matches" replace />;
  return (
    <Scorer
      matchId={matchId}
      onLeave={() => {
        // Back where you came from, or the list if this was a deep link.
        const from = (location.state as { from?: string } | null)?.from;
        navigate(from ?? "/matches");
      }}
    />
  );
}

function MatchesRoute({ clubId, canScore }: { clubId: string; canScore: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <Matches clubId={clubId} canScore={canScore}
      onOpen={(matchId) => navigate(`/score/${matchId}`, { state: { from: location.pathname } })} />
  );
}

/* ── screens ────────────────────────────────────────────────────────────── */

function Home({ club, user }: { club: Club; user: User }) {
  const [live, setLive] = useState<LiveCard[]>([]);
  const [counts, setCounts] = useState<{ people: number; teams: number; matches: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [c, m] = await Promise.all([
        api<{ people: unknown[]; teams: unknown[] }>(`/api/v1/clubs/${club.id}`),
        api<{ matches: { id: string; matchId: string; status: string;
                         homeShort: string; awayShort: string }[] }>(`/api/v1/clubs/${club.id}/matches`),
      ]);
      if (!alive) return;
      if (!c.ok || !m.ok) { setFailed(true); return; }
      setCounts({ people: c.data.people.length, teams: c.data.teams.length, matches: m.data.matches.length });

      // Pull a snapshot for each live match so the card shows a real score
      // rather than just "in progress".
      const running = m.data.matches.filter((x) => x.status === "LIVE").slice(0, 4);
      const cards = await Promise.all(running.map(async (x) => {
        const s2 = await api<{
          state: { innings: { battingTeamId: string; totalRuns: number; wickets: number;
                              legalBalls: number }[]; currentInnings: number };
          meta: { teams: Record<string, { short: string }>; ruleset: { ballsPerOver: number; oversPerInnings: number } };
        }>(`/api/v1/matches/${x.matchId}/snapshot`);
        if (!s2.ok) return null;
        const inn = s2.data.state.innings[s2.data.state.currentInnings];
        return {
          matchId: x.matchId,
          home: x.homeShort, away: x.awayShort,
          batting: inn ? s2.data.meta.teams[inn.battingTeamId]?.short ?? "" : "",
          runs: inn?.totalRuns ?? 0, wickets: inn?.wickets ?? 0,
          balls: inn?.legalBalls ?? 0,
          per: s2.data.meta.ruleset.ballsPerOver,
          overs: s2.data.meta.ruleset.oversPerInnings,
        } as LiveCard;
      }));
      if (alive) setLive(cards.filter(Boolean) as LiveCard[]);
    };
    void load();
    const t = setInterval(() => { if (navigator.onLine) void load(); }, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [club.id]);

  return (
    <div className="mx-auto w-full max-w-[860px] px-4 py-6 split:px-6">
      <h1 className="font-num text-score font-semibold">{club.name}</h1>
      <p className="mt-1 text-body text-faint">
        {user.displayName} · {club.role.toLowerCase()}
      </p>

      {live.length > 0 && (
        <>
          <h2 className="mb-2 mt-6 font-mono text-label uppercase tracking-[.14em] text-faint">
            Live now
          </h2>
          <div className="grid gap-3 split:grid-cols-2">
            {live.map((m) => (
              <button key={m.matchId} onClick={() => navigate(`/score/${m.matchId}`)}
                className="rounded-xl border border-line border-l-[3px] border-l-leather bg-panel
                           px-4 py-3.5 text-left hover:bg-raise">
                <div className="flex items-center gap-2 font-mono text-label tracking-[.16em] text-leather">
                  <IconLive />LIVE
                  <span className="ml-auto text-faint">{m.home} v {m.away}</span>
                </div>
                <div className="mt-2 flex items-baseline gap-2.5">
                  <span className="font-mono text-meta tracking-[.13em] text-faint">{m.batting}</span>
                  <span className="font-num text-big font-semibold leading-none">
                    {m.runs}<span className="text-[#8B95A1]">/{m.wickets}</span>
                  </span>
                  <span className="ml-auto font-mono text-caption text-faint">
                    {Math.floor(m.balls / m.per)}.{m.balls % m.per} / {m.overs} ov
                  </span>
                </div>
                  <div className="mt-2.5 flex gap-2">
                    <span className="flex min-h-[38px] items-center gap-1.5 rounded-full border
                                     border-line px-3 font-mono text-label text-faint">
                      <IconChart size={14} /> SCORE
                    </span>
                    <span onClick={(e) => { e.stopPropagation(); navigate(`/watch/${m.matchId}`); }}
                      className="flex min-h-[38px] items-center gap-1.5 rounded-full border border-line
                                 px-3 font-mono text-label text-faint hover:border-amber hover:text-amber">
                      <IconWatch size={14} /> WATCH
                    </span>
                  </div>
              </button>
            ))}
          </div>
        </>
      )}

      <h2 className="mb-2 mt-6 font-mono text-label uppercase tracking-[.14em] text-faint">The club</h2>
      {failed ? (
        <Card><Empty title="Could not load">The club did not respond. Try again shortly.</Empty></Card>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          <button onClick={() => navigate("/people")} className="text-left">
            <Tile n={counts?.people} label="People" Icon={IconPeople} />
          </button>
          <button onClick={() => navigate("/teams")} className="text-left">
            <Tile n={counts?.teams} label="Teams" Icon={IconTeam} />
          </button>
          <button onClick={() => navigate("/matches")} className="text-left">
            <Tile n={counts?.matches} label="Matches" Icon={IconMatch} />
          </button>
        </div>
      )}

      <div className="mt-5 grid gap-2 split:grid-cols-2">
        <Button full onClick={() => navigate("/matches")}>Start a match</Button>
        <Button full kind="ghost" onClick={() => navigate("/cups")}>Tournaments</Button>
      </div>
    </div>
  );
}

interface LiveCard {
  matchId: string; home: string; away: string; batting: string;
  runs: number; wickets: number; balls: number; per: number; overs: number;
}

function Tile({ n, label, Icon }: {
  n: number | undefined; label: string;
  Icon?: (p: { size?: number }) => React.ReactElement;
}) {
  return (
    <div className="rounded-xl border border-line bg-panel p-4">
      {Icon && <div className="mb-2 text-faint"><Icon size={18} /></div>}
      <div className="font-num text-big font-semibold leading-none">{n ?? "…"}</div>
      <div className="mt-1 font-mono text-label uppercase tracking-[.12em] text-faint">{label}</div>
    </div>
  );
}

function More({ user, club, onSignedOut }: { user: User; club: Club; onSignedOut: () => void }) {
  const [lang, setLang] = useState<Lang>("en");
  const [voiceOn, setVoiceOn] = useState(false);

  return (
    <Screen title="Settings">
      <h2>Commentary</h2>
      <div className="rounded-xl border border-line bg-panel p-4">
        {/* The same control the scorer uses, so the choice is made once and in
            an obvious place rather than only mid-match. */}
        <VoiceBar lang={lang} setLang={setLang} voiceOn={voiceOn} setVoiceOn={setVoiceOn} expanded />
        <p className="mt-3 text-caption leading-relaxed text-faint">
          Set this up before a match rather than while someone is waiting to bowl —
          the first play has to download the lines, and turning it on needs a tap
          the browser can see.
        </p>
      </div>

      <h2>Offline</h2>
      <OfflineAudio clubId={club.id} />

      <h2>Club</h2>
      <ClubSettings club={club} />

      {/* Owners and admins only, matching the endpoint. Showing the heading to
          everyone else would just promise a panel that answers 403. */}
      {["OWNER", "ADMIN"].includes(club.role) && (
        <>
          <h2>Activity</h2>
          <ActivityLog clubId={club.id} />
        </>
      )}

      <h2>Account</h2>
      <Card>
        <Row><div className="flex-1"><b className="block">{user.displayName}</b>
          <span className="font-mono text-caption text-faint">{user.email}</span></div></Row>
        <Row>
          <div className="flex-1">
            <b className="block">Version</b>
            <span className="font-mono text-caption text-faint">{BUILD}</span>
          </div>
          {/* Was 36x15. The label stays small; the hit area does not. */}
          <button onClick={() => void update()}
            className="-my-2 min-h-[44px] px-2 font-mono text-label uppercase
                       tracking-[.12em] text-faint">
            Check
          </button>
        </Row>
      </Card>

      <h2>Unsent deliveries</h2>
      <PendingRescue />

      <div className="mt-4">
        <Button full kind="danger"
          onClick={async () => {
            await api("/api/v1/auth/logout", { method: "POST" });
            setToken(null);          // the bearer copy must go too
            onSignedOut();
          }}>
          Sign out
        </Button>
      </div>
    </Screen>
  );
}


/* ── club settings ──────────────────────────────────────────────────────── */

function ClubSettings({ club }: { club: Club }) {
  const [name, setName] = useState(club.name);
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();
  const [msg, setMsg] = useState<string | null>(null);
  const canEdit = ["OWNER", "ADMIN", "ORGANIZER"].includes(club.role);
  const canEditHere = canEdit;

  const save = async () => {
    const r = await api<{ error?: string }>(`/api/v1/clubs/${club.id}`,
      { method: "PATCH", json: { name: name.trim() } });
    if (r.ok) {
      setEditing(false); setMsg(null);
      invalidate("club", `club:${club.id}`);
      // The name is in the header and in every cached response.
      window.location.reload();
    } else setMsg(r.data.error ?? "Could not rename the club.");
  };

  return (
    <Card>
      {editing ? (
        <div className="space-y-3 p-4">
          <Field label="Club name" value={name} maxLength={60}
                 onChange={(e) => setName(e.target.value)} />
          {msg && <p className="text-caption text-leather">{msg}</p>}
          <div className="grid grid-cols-2 gap-2">
            <Button full kind="ghost" onClick={() => { setName(club.name); setEditing(false); }}>
              Cancel
            </Button>
            <Button full disabled={!name.trim() || name.trim() === club.name} onClick={save}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Row {...(canEdit ? { onClick: () => setEditing(true) } : {})}>
            <div className="min-w-0 flex-1">
              <b className="block truncate">{club.name}</b>
              <span className="font-mono text-caption text-faint">
                you are {club.role.toLowerCase()}
              </span>
            </div>
            {canEdit && <span className="text-faint"><IconChevron size={17} /></span>}
          </Row>

          {/* Only owners and admins: seeing the member list means seeing
              everyone's email, which is not a viewer's business. */}
          {canEditHere && (
            <Row onClick={() => navigate("/repair")}>
              <div className="min-w-0 flex-1">
                <b className="block truncate">Repair</b>
                <span className="font-mono text-caption text-faint">
                  rebuild scorecards, close stuck matches
                </span>
              </div>
              <span className="text-faint"><IconChevron size={17} /></span>
            </Row>
          )}

          {["OWNER", "ADMIN"].includes(club.role) && (
            <Row onClick={() => navigate("/members")}>
              <div className="min-w-0 flex-1">
                <b className="block truncate">Members</b>
                <span className="font-mono text-caption text-faint">who can do what</span>
              </div>
              <span className="text-faint"><IconChevron size={17} /></span>
            </Row>
          )}
        </>
      )}
    </Card>
  );
}

/* ── activity ───────────────────────────────────────────────────────────── */

interface LogEntry {
  id: string; action: string; subject: string | null;
  detail: Record<string, unknown> | null; at: number; who: string | null;
}

/** What each action is called, and the tone it should read in. */
const ACTION: Record<string, { label: string; tone?: "warn" | "bad" }> = {
  MATCH_STARTED:        { label: "started a match" },
  MATCH_DELETED:        { label: "deleted a match", tone: "bad" },
  LEASE_FORCED:         { label: "took the scoring pen", tone: "warn" },
  PLAYER_TRANSFERRED:   { label: "transferred a player" },
  TEAM_CREATED:         { label: "created a team" },
  TEAM_RENAMED:         { label: "renamed a team" },
  TEAM_ARCHIVED:        { label: "archived a team", tone: "warn" },
  PERSON_ADDED:         { label: "added a person" },
  PERSON_ARCHIVED:      { label: "archived a person", tone: "warn" },
  TOURNAMENT_CREATED:   { label: "created a tournament" },
  TOURNAMENT_EDITED:    { label: "edited a tournament" },
  TOURNAMENT_PUBLISHED: { label: "published a tournament" },
  RULESET_CREATED:      { label: "added a ruleset" },
  RULESET_EDITED:       { label: "edited a ruleset" },
  RULESET_VERSIONED:    { label: "branched a ruleset" },
  CLUB_RENAMED:         { label: "renamed the club" },
  AUDIO_UPLOADED:       { label: "uploaded audio" },
  AUDIO_DELETED:        { label: "deleted audio", tone: "warn" },
  REPROJECTED:          { label: "rebuilt the statistics" },
};

function ActivityLog({ clubId }: { clubId: string }) {
  const [rows, setRows] = useState<LogEntry[] | null>(null);
  const [all, setAll] = useState(false);

  useEffect(() => {
    void api<{ entries: LogEntry[] }>(`/api/v1/clubs/${clubId}/log`)
      .then((r) => setRows(r.ok ? r.data.entries : []));
  }, [clubId]);

  if (!rows) return <Card><Empty title="Loading…" /></Card>;
  if (!rows.length) {
    return <Card><Empty title="Nothing yet">Changes to the club appear here.</Empty></Card>;
  }

  const shown = all ? rows : rows.slice(0, 8);
  return (
    <>
      <Card>
        {shown.map((e) => {
          const a = ACTION[e.action] ?? { label: e.action.toLowerCase().replace(/_/g, " ") };
          const detail = summarise(e);
          return (
            <div key={e.id} className="flex gap-3 border-b border-line px-4 py-2.5 last:border-b-0">
              <span className={`mt-[3px] h-2 w-2 shrink-0 rounded-full
                ${a.tone === "bad" ? "bg-leather" : a.tone === "warn" ? "bg-amber" : "bg-crease"}`} />
              <div className="min-w-0 flex-1">
                <span className="text-body">
                  <b className="font-semibold">{e.who ?? "someone"}</b> {a.label}
                  {detail && <span className="text-faint"> · {detail}</span>}
                </span>
              </div>
              <span className="shrink-0 font-mono text-meta text-faint">{ago(e.at)}</span>
            </div>
          );
        })}
      </Card>
      {rows.length > 8 && (
        <button onClick={() => setAll((v) => !v)}
          className="mt-2 min-h-[44px] w-full text-center font-mono text-meta uppercase
                     tracking-[.13em] text-faint">
          {all ? "Show less" : `Show all ${rows.length}`}
        </button>
      )}
    </>
  );
}

/** Pull the one useful fact out of the detail blob, if there is one. */
function summarise(e: LogEntry): string | null {
  const d = e.detail as Record<string, string | number> | null;
  if (!d) return null;
  if (typeof d["teams"] === "string") return d["teams"];
  if (typeof d["name"] === "string") return d["name"];
  if (typeof d["title"] === "string") return d["title"];
  if (typeof d["to"] === "string" && typeof d["from"] === "string") return `${d["from"]} → ${d["to"]}`;
  if (typeof d["rebuilt"] === "number") return `${d["rebuilt"]} fixtures`;
  return null;
}

function ago(at: number): string {
  const s = Math.max(0, (Date.now() - at) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}


/**
 * Get stranded deliveries off the phone.
 *
 * When a match looks short of balls the first question is whether they are
 * still queued here or were never recorded, and until now the only way to
 * answer it was to read the source. This says how many are waiting and lets
 * them be saved as a file, so a scorer standing at a ground can send it on
 * rather than lose them.
 */
function PendingRescue() {
  const [n, setN] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const read = () => { void countPending().then(setN); };
    read();
    const t = setInterval(read, 5000);
    return () => clearInterval(t);
  }, []);

  const save = async () => {
    const json = await exportAllPending();
    const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `orbit-unsent-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setSaved(true);
  };

  return (
    <Card>
      <Row>
        <div className="min-w-0 flex-1">
          <b className="block">{n === null ? "Checking…" : n === 0 ? "Nothing waiting" : `${n} waiting to send`}</b>
          <span className="text-caption leading-relaxed text-faint">
            {n === 0
              ? "Every delivery scored on this phone has reached the server."
              : "These were scored here and have not reached the server yet. They send on their own once you are online and hold the scoring lease."}
          </span>
        </div>
      </Row>
      {n !== null && n > 0 && (
        <div className="p-4 pt-0">
          <Button full kind="ghost" onClick={() => void save()}>
            {saved ? "Saved" : "Save them to a file"}
          </Button>
          <p className="mt-1.5 text-meta leading-relaxed text-faint">
            A copy you can send on if this phone cannot reach the server. Saving does
            not remove them — they will still send by themselves.
          </p>
        </div>
      )}
    </Card>
  );
}
